import type { ClientSummary, CommunicationCounts, PhoneEventView } from "@vaulted/shared";
import { formatPhone } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { addClientLink, callerIdLabel } from "../components/IncomingCalls";
import { errorMessage, get, post } from "../api";
import { useSession } from "../session";
import { Alert, Badge, Button, Checkbox, Field, Loading, Panel, PanelHeader, cx, formatDateTime } from "../components/ui";

/** Texts needing an answer and unread calls (the menu badge). */
export function useCommCounts() {
  const { can } = useSession();
  return useQuery({ queryKey: ["comm", "counts"], queryFn: () => get<CommunicationCounts>("/api/communications/counts"), enabled: can("calls.view"), refetchInterval: 60_000 });
}

const seconds = (s: number | null) => (s == null ? "" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`);

type Filter = "unread" | "all" | "missed" | "voicemail";
const FILTERS: { key: Filter; label: string }[] = [
  { key: "unread", label: "Unread" },
  { key: "all", label: "All" },
  { key: "missed", label: "Missed" },
  { key: "voicemail", label: "Voicemail" },
];

/** Every call on the clinic's lines, newest first (the Communication page's Phone tab). */
export function PhoneList() {
  const [filter, setFilter] = useState<Filter>("unread");
  const [showRead, setShowRead] = useState(false);
  const list = useQuery({ queryKey: ["calls", filter], queryFn: () => get<PhoneEventView[]>(`/api/calls?filter=${filter}`), refetchInterval: 60_000 });
  // Voicemail and Missed show what's still waiting unless asked for the read ones too.
  const hidesRead = (filter === "voicemail" || filter === "missed") && !showRead;
  const items = hidesRead ? (list.data ?? []).filter((e) => !e.handledAt) : (list.data ?? []);
  const readCount = (list.data ?? []).filter((e) => e.handledAt).length;
  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-ink-soft">
        Checked every couple of minutes. Unread: voicemails, missed calls, and answered calls from numbers nobody knows yet. Mark each one read once it's taken care of.
      </p>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Show">
        {FILTERS.map((f) => (
          <Button key={f.key} variant={filter === f.key ? "primary" : "secondary"} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}>
            {f.label}
          </Button>
        ))}
        {(filter === "voicemail" || filter === "missed") && (
          <span className="ml-auto self-center">
            <Checkbox label={`Show read too${readCount ? ` (${readCount})` : ""}`} checked={showRead} onChange={setShowRead} />
          </span>
        )}
      </div>
      <Panel>
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <Alert>{errorMessage(list.error)}</Alert>
        ) : items.length ? (
          <ul className="px-5 py-2">
            {items.map((e) => (
              <CallItem key={e.id} e={e} showClient />
            ))}
          </ul>
        ) : (
          <p className="px-5 py-5 text-sm text-muted">{filter === "unread" || hidesRead ? `All caught up.${hidesRead && readCount ? " Tick “Show read too” to see earlier ones." : ""}` : "Nothing yet."}</p>
        )}
      </Panel>
    </div>
  );
}

const KIND: Record<PhoneEventView["kind"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  voicemail: { label: "Voicemail", tone: "brand" },
  missed_call: { label: "Missed", tone: "wait" },
  answered: { label: "Answered", tone: "ok" },
  outgoing: { label: "Outgoing", tone: "off" },
};

/**
 * A number that isn't a client: name it (a vendor, a doctor's office) so it shows by name, or block it
 * as spam (no pop-up; its calls and texts are marked read as they come in).
 */
export function NumberActions({ phone, onDone }: { phone: string; onDone?: () => void }) {
  const qc = useQueryClient();
  const [naming, setNaming] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (v: { name: string; blocked: boolean }) => post("/api/numbers", { phone, ...v }),
    onSuccess: async () => {
      setNaming(null);
      await Promise.all(["calls", "comm", "client-calls", "calls-ringing", "numbers"].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      onDone?.();
    },
  });
  return (
    <div className="flex flex-col gap-2">
      {naming == null ? (
        <div className="flex flex-wrap gap-2">
          <Button variant="quiet" onClick={() => setNaming("")}>
            Not a client
          </Button>
          <Button
            variant="quiet"
            busy={save.isPending}
            onClick={() => window.confirm(`Block ${formatPhone(phone)} as spam? Its calls go straight to voicemail (nobody's phone rings), it won't pop up, and its calls and texts will be marked read. You can unblock it in Settings → Phone.`) && save.mutate({ name: "", blocked: true })}
          >
            Block
          </Button>
        </div>
      ) : (
        <form
          className="flex max-w-md flex-wrap items-end gap-2"
          onSubmit={(ev) => {
            ev.preventDefault();
            save.mutate({ name: naming, blocked: false });
          }}
        >
          <Field label="Who is it? (optional)" placeholder="McKesson, Dr. Lee's office" maxLength={80} value={naming} onChange={(ev) => setNaming(ev.target.value)} className="min-w-56 flex-1" />
          <Button variant="secondary" type="submit" busy={save.isPending}>
            Save
          </Button>
          <Button variant="quiet" type="button" onClick={() => setNaming(null)}>
            Cancel
          </Button>
        </form>
      )}
      {save.error && <Alert>{errorMessage(save.error)}</Alert>}
    </div>
  );
}

/** One call: who, when, which way, the recording and transcript for a voicemail, and what staff can do. */
export function CallItem({ e, showClient }: { e: PhoneEventView; showClient?: boolean }) {
  const qc = useQueryClient();
  const refresh = () => Promise.all([qc.invalidateQueries({ queryKey: ["calls"] }), qc.invalidateQueries({ queryKey: ["comm"] }), qc.invalidateQueries({ queryKey: ["client-calls"] })]);
  const handled = useMutation({ mutationFn: () => post(`/api/calls/${e.id}/handled`, {}), onSuccess: refresh });
  const unread = useMutation({ mutationFn: () => post(`/api/calls/${e.id}/unread`, {}), onSuccess: refresh });
  // Voicemails and missed calls wait for someone: unread ones stand out, read ones fade.
  const waits = e.kind === "voicemail" || e.kind === "missed_call";
  const isNew = waits && !e.handledAt;
  const [savePhone, setSavePhone] = useState(true);
  const link = useMutation({ mutationFn: (clientId: string) => post(`/api/calls/${e.id}/link`, { clientId, savePhone }), onSuccess: refresh });
  const unlink = useMutation({ mutationFn: () => post("/api/communications/unlink", { from: e.from, clientId: e.client!.id }), onSuccess: refresh });
  const [linking, setLinking] = useState(false);
  const who = e.client ? e.client.name : e.known ? e.known.name || "Not a client" : e.callerName ? callerIdLabel(e.callerName) : e.direction === "out" ? "Unknown number" : "Unknown caller";
  const { can } = useSession();
  // Answered and outgoing calls are read on their own; only a person's "read" is worth showing.
  const showRead = e.handledAt && (e.handledBy || e.kind === "voicemail" || e.kind === "missed_call");
  return (
    <li className={cx("flex flex-col gap-2 border-b border-mist py-3 text-sm last:border-0", isNew && "-mx-3 border-l-4 border-l-brand bg-brand/5 px-3", waits && !isNew && "opacity-70")}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {isNew && <span className="rounded-full bg-brand px-2 py-0.5 text-xs font-extrabold text-white">New</span>}
        {waits && !isNew && <span className="rounded-full bg-mist px-2 py-0.5 text-xs font-semibold text-muted">✓ Read</span>}
        <span className="text-muted" aria-label={e.direction === "out" ? "Outgoing" : "Incoming"} title={e.direction === "out" ? "Outgoing" : "Incoming"}>
          {e.direction === "out" ? "↗" : "↙"}
        </span>
        <Badge tone={KIND[e.kind].tone}>{KIND[e.kind].label}</Badge>
        {e.known?.blocked && <Badge tone="off">Blocked</Badge>}
        {showClient && e.client ? (
          <Link to={`/clients/${e.client.id}`} className="font-semibold text-brand underline">
            {who}
          </Link>
        ) : (
          showClient && <span className="font-semibold">{who}</span>
        )}
        <span className="text-ink-soft">{formatPhone(e.from)}</span>
        {e.durationSeconds != null && <span className="text-muted">{seconds(e.durationSeconds)}</span>}
        <span className="ml-auto text-muted">{formatDateTime(e.at)}</span>
      </div>
      {e.hasAudio && <audio controls preload="none" src={`/api/calls/${e.id}/audio`} className="w-full max-w-md" />}
      {e.transcript ? (
        <p className="whitespace-pre-wrap text-ink-soft">“{e.transcript}”</p>
      ) : (
        e.kind === "voicemail" && <p className="text-xs text-muted">{e.transcriptStatus === "pending" ? "Transcript on its way." : "No transcript."}</p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {e.handledAt ? (
          showRead && (
            <span className="flex items-center gap-2 text-xs text-muted">
              Read {formatDateTime(e.handledAt)}
              {e.handledBy ? ` by ${e.handledBy}` : ""}
              {waits && (
                <button type="button" className="font-semibold text-brand hover:underline" disabled={unread.isPending} onClick={() => unread.mutate()}>
                  Mark unread
                </button>
              )}
            </span>
          )
        ) : (
          <Button variant="secondary" busy={handled.isPending} onClick={() => handled.mutate()}>
            Mark read
          </Button>
        )}
        {e.client && e.unlinkable && (
          <Button
            variant="quiet"
            busy={unlink.isPending}
            onClick={() => window.confirm(`Unlink ${formatPhone(e.from)} from ${e.client!.name}? Its calls and texts go back to unknown.`) && unlink.mutate()}
          >
            Not them? Unlink
          </Button>
        )}
        {showClient && !e.client && (
          <>
            {e.possibleClients.map((c) => (
              <Button key={c.id} variant="quiet" busy={link.isPending} onClick={() => link.mutate(c.id)}>
                It was {c.name}
              </Button>
            ))}
            <Button variant="quiet" onClick={() => setLinking(!linking)}>
              {linking ? "Close" : "Link to a client"}
            </Button>
            {!e.known && can("clients.edit") && (
              <Link to={addClientLink(e.from, e.direction === "in" ? e.callerName : null)} className="px-2 text-sm font-semibold text-brand hover:underline">
                Add client
              </Link>
            )}
          </>
        )}
      </div>
      {showClient && !e.client && (e.possibleClients.length > 0 || linking) && <SavePhoneBox checked={savePhone} onChange={setSavePhone} />}
      {showClient && !e.client && !e.known && <NumberActions phone={e.from} />}
      {linking && <ClientPicker onPick={(id) => link.mutate(id, { onSuccess: () => setLinking(false) })} />}
      {(handled.error ?? unread.error ?? link.error ?? unlink.error) && <Alert>{errorMessage(handled.error ?? unread.error ?? link.error ?? unlink.error)}</Alert>}
    </li>
  );
}

/** When linking a call or text to a client: also keep the number on their profile (so their calls pop up). */
export function SavePhoneBox({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="max-w-md">
      <Checkbox label="Save this number to their profile" description="Calls and texts from it will show as theirs." checked={checked} onChange={onChange} />
    </div>
  );
}

export function ClientPicker({ onPick }: { onPick: (id: string) => void }) {
  const [q, setQ] = useState("");
  const results = useQuery({ queryKey: ["call-client-search", q], queryFn: () => get<ClientSummary[]>(`/api/clients?q=${encodeURIComponent(q)}`), enabled: q.trim().length >= 2 });
  return (
    <div className="flex max-w-md flex-col gap-2">
      <Field label="Find the client" type="search" placeholder="Name, phone, or email" value={q} onChange={(e) => setQ(e.target.value)} />
      <ul className="flex flex-col">
        {(results.data ?? []).slice(0, 8).map((c) => (
          <li key={c.id}>
            <button className="py-1 text-left font-semibold text-brand hover:underline" onClick={() => onPick(c.id)}>
              {c.preferredName || c.firstName} {c.lastName}
              <span className="ml-2 font-normal text-muted">{c.mobile ? formatPhone(c.mobile) : c.email}</span>
            </button>
          </li>
        ))}
      </ul>
      <p className="text-xs text-muted">
        Someone new? <Link to="/clients" className="underline">Add them on Clients</Link>, then link this call.
      </p>
    </div>
  );
}

/** On the client's Communications tab: their calls, voicemails, and missed calls. */
export function ClientCalls({ clientId }: { clientId: string }) {
  const { can } = useSession();
  const list = useQuery({ queryKey: ["client-calls", clientId], queryFn: () => get<PhoneEventView[]>(`/api/clients/${clientId}/calls`), enabled: can("calls.view") });
  if (!can("calls.view") || !list.data?.length) return null;
  return (
    <Panel>
      <PanelHeader title="Calls" />
      <ul className="px-5 pb-3">
        {list.data.map((e) => (
          <CallItem key={e.id} e={e} />
        ))}
      </ul>
    </Panel>
  );
}
